import React from "react";
import { Stack, TextField, Typography, FormControlLabel, Switch } from "@mui/material";

const normalizeArray = (a) => (Array.isArray(a) ? a : []);

const QuestionFormDropdown = ({ value, onChange }) => {
    const v = value || {};
    const parts = normalizeArray(v.dropdownQuestionParts);
    const options = normalizeArray(v.options);

    const correctAnswers =
        v.correctAnswers && typeof v.correctAnswers === "object" ? v.correctAnswers : {};

    function setPart(idx, nextVal) {
        const next = parts.map((p, i) => (i === idx ? nextVal : p));
        onChange({ ...v, dropdownQuestionParts: next });
    }

    function addTextPart() {
        onChange({ ...v, dropdownQuestionParts: [...parts, ""] });
    }

    function addDropdownPart() {
        onChange({
            ...v,
            dropdownQuestionParts: [...parts, { id: `dropdown${parts.length + 1}`, text: "" }],
        });
    }

    function removePart(idx) {
        const next = parts.filter((_, i) => i !== idx);
        onChange({ ...v, dropdownQuestionParts: next });
    }

    function setOption(idx, patch) {
        const next = options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
        onChange({ ...v, options: next });
    }

    function addOption() {
        onChange({ ...v, options: [...options, { id: "", text: "" }] });
    }

    function removeOption(idx) {
        onChange({ ...v, options: options.filter((_, i) => i !== idx) });
    }

    function setCorrectAnswer(dropdownId, answerId) {
        onChange({
            ...v,
            correctAnswers: { ...correctAnswers, [dropdownId]: answerId },
        });
    }

    const dropdownIds = parts
        .filter((p) => p && typeof p === "object" && p.id)
        .map((p) => String(p.id));

    const dropdownAmountValue = (v.dropdownAmount ?? dropdownIds.length) || 0;

    return (
        <Stack spacing={2}>
            <TextField
                label="Підзаголовок"
                value={v.subtitle || ""}
                onChange={(e) => onChange({ ...v, subtitle: e.target.value })}
                helperText="Необов'язково. Показується під назвою модуля."
                fullWidth
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={Boolean(v.showAnswerOptions)}
                        onChange={(e) => onChange({ ...v, showAnswerOptions: e.target.checked })}
                    />
                }
                label="Показувати варіанти відповідей"
            />

            <TextField
                label="Кількість випадаючих списків"
                type="number"
                value={dropdownAmountValue}
                onChange={(e) =>
                    onChange({ ...v, dropdownAmount: Number(e.target.value) || 0 })
                }
                helperText="Зазвичай дорівнює кількості вставлених dropdown-частин."
                fullWidth
            />

            <Typography variant="subtitle2">Частини питання</Typography>

            {parts.map((p, idx) => {
                const isObj = p && typeof p === "object";
                return (
                    <Stack key={idx} direction={{ xs: "column", sm: "row" }} spacing={1}>
                        {isObj ? (
                            <>
                                <TextField
                                    label="ID випадаючого списку"
                                    value={p.id || ""}
                                    onChange={(e) => setPart(idx, { ...p, id: e.target.value })}
                                    helperText="Наприклад: dropdown1."
                                    sx={{ width: { sm: 180 } }}
                                />
                                <TextField
                                    label="Текст плейсхолдера"
                                    value={p.text || ""}
                                    onChange={(e) => setPart(idx, { ...p, text: e.target.value })}
                                    helperText="Текст, який буде показано на місці списку."
                                    fullWidth
                                />
                            </>
                        ) : (
                            <TextField
                                label="Текст / HTML"
                                value={String(p)}
                                onChange={(e) => setPart(idx, e.target.value)}
                                helperText="Фрагмент тексту між випадаючими списками."
                                fullWidth
                            />
                        )}
                        <TextField
                            value="✕"
                            onClick={() => removePart(idx)}
                            sx={{ width: { sm: 60 }, "& input": { cursor: "pointer", textAlign: "center" } }}
                            inputProps={{ readOnly: true }}
                        />
                    </Stack>
                );
            })}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                    value="+ додати текст"
                    onClick={addTextPart}
                    inputProps={{ readOnly: true }}
                    fullWidth
                    sx={{ "& input": { cursor: "pointer" } }}
                />
                <TextField
                    value="+ додати список"
                    onClick={addDropdownPart}
                    inputProps={{ readOnly: true }}
                    fullWidth
                    sx={{ "& input": { cursor: "pointer" } }}
                />
            </Stack>

            <Typography variant="subtitle2">Варіанти відповідей</Typography>

            {options.map((o, idx) => (
                <Stack key={idx} direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                        label="ID"
                        value={o.id || ""}
                        onChange={(e) => setOption(idx, { id: e.target.value })}
                        helperText="Код варіанта."
                        sx={{ width: { sm: 120 } }}
                    />
                    <TextField
                        label="Текст варіанта"
                        value={o.text || ""}
                        onChange={(e) => setOption(idx, { text: e.target.value })}
                        helperText="Текст, який учень обирає у списку."
                        fullWidth
                    />
                    <TextField
                        value="✕"
                        onClick={() => removeOption(idx)}
                        sx={{ width: { sm: 60 }, "& input": { cursor: "pointer", textAlign: "center" } }}
                        inputProps={{ readOnly: true }}
                    />
                </Stack>
            ))}

            <TextField
                value="+ додати варіант"
                onClick={addOption}
                inputProps={{ readOnly: true }}
                sx={{ "& input": { cursor: "pointer" } }}
            />

            <Typography variant="subtitle2">Правильні відповіді</Typography>

            {dropdownIds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Додайте частини зі списками та ID, наприклад dropdown1. Після цього тут з'являться поля правильних відповідей.
                </Typography>
            ) : (
                dropdownIds.map((did) => (
                    <TextField
                        key={did}
                        label={did}
                        value={correctAnswers[did] || ""}
                        onChange={(e) => setCorrectAnswer(did, e.target.value)}
                        placeholder="А / Б / В / Г ..."
                        helperText="Вкажіть ID правильного варіанта для цього списку."
                        fullWidth
                    />
                ))
            )}
        </Stack>
    );
};

export default QuestionFormDropdown;
